import type { BoardObjectV2 } from "@bezpart-board/shared";
import {
  boardObjectsFromClipboardData,
  hasInternalClipboard,
  imageFileFromClipboardData,
  loadClipboardFromPayload,
  plainTextFromClipboardData,
  resolvePasteHref,
} from "../../editor/clipboard";

/** Минимальный API сцены для paste (WbBoardScene). */
export type BoardPasteScene = {
  stageCenterPoint: () => { x: number; y: number };
  pasteAnchorPoint?: () => { x: number; y: number };
  uploadImageFile: (file: File, x: number, y: number) => Promise<unknown>;
  pasteClipboard: () => Promise<unknown>;
  addTextAt: (
    x: number,
    y: number,
    text: string,
    opts?: { href?: string | null }
  ) => Promise<unknown>;
};

function pastePoint(scene: BoardPasteScene): { x: number; y: number } {
  if (typeof scene.pasteAnchorPoint === "function") {
    return scene.pasteAnchorPoint();
  }
  return scene.stageCenterPoint();
}

/**
 * Универсальная вставка: image → объекты доски → текст/URL → internal clipboard.
 * @returns true если событие обработано (preventDefault уже вызван).
 */
export function handleBoardClipboardPaste(
  ev: ClipboardEvent,
  scene: BoardPasteScene,
  opts: { switchToSelect: () => void }
): boolean {
  const image = imageFileFromClipboardData(ev.clipboardData);
  if (image) {
    ev.preventDefault();
    const pt = pastePoint(scene);
    opts.switchToSelect();
    void scene.uploadImageFile(image, pt.x, pt.y);
    return true;
  }

  const boardObjects = boardObjectsFromClipboardData(ev.clipboardData);
  if (boardObjects?.length) {
    ev.preventDefault();
    loadClipboardFromPayload(boardObjects as BoardObjectV2[]);
    opts.switchToSelect();
    void scene.pasteClipboard();
    return true;
  }

  const plain = plainTextFromClipboardData(ev.clipboardData);
  if (plain) {
    ev.preventDefault();
    const pt = pastePoint(scene);
    opts.switchToSelect();
    void scene.addTextAt(pt.x, pt.y, plain, { href: resolvePasteHref(plain) });
    return true;
  }

  if (hasInternalClipboard()) {
    ev.preventDefault();
    opts.switchToSelect();
    void scene.pasteClipboard();
    return true;
  }

  return false;
}
